import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { API } from "@/lib/api";
import { useSuggestions } from "@/context/SuggestionsContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useI18n } from "@/context/I18nContext";
import { SupportCard } from "@/components/SupportCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PrivacyNote } from "@/components/PrivacyNote";
import { HeartPulse, Send, Brain, Check, X, Sparkles } from "lucide-react";
import { QuotePanel } from "@/components/QuotePanel";

const TEXTURE = "https://images.unsplash.com/photo-1629654857513-1136aef1b10f";

function Bubble({ role, children }) {
  const isUser = role === "user";
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] whitespace-pre-wrap rounded-3xl px-5 py-3 text-sm leading-relaxed ${
        isUser ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md glass border border-border/60"
      }`}>
        {children || <span className="inline-flex gap-1"><Dot /><Dot d={0.2} /><Dot d={0.4} /></span>}
      </div>
    </motion.div>
  );
}
const Dot = ({ d = 0 }) => <span className="h-1.5 w-1.5 animate-breathe rounded-full bg-current" style={{ animationDelay: `${d}s` }} />;

export default function Companion() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [starters, setStarters] = useState([]);
  const [support, setSupport] = useState(null);
  const endRef = useRef(null);
  const { refresh: refreshBadge } = useSuggestions();
  const { sub, premium, refresh: refreshSub } = useSubscription();
  const { t } = useI18n();
  const navigate = useNavigate();
  const remaining = sub && !premium ? Math.max(0, (sub.chat_limit ?? 5) - (sub.chat_used_today ?? 0)) : null;

  useEffect(() => {
    api.get("/companion/history").then(({ data }) => setMessages(data.messages || [])).catch(() => {});
    api.get("/memory-suggestions").then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => {});
    api.get("/companion/starters").then(({ data }) => setStarters(data.starters || [])).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming, suggestions]);

  const acceptMemory = async (s) => {
    setSuggestions((prev) => prev.filter((x) => x.suggestion_id !== s.suggestion_id));
    try { await api.post(`/memory-suggestions/${s.suggestion_id}/accept`); toast.success("I'll remember that 🌿"); }
    catch { toast.error("Couldn't save that memory"); }
    finally { refreshBadge(); }
  };
  const dismissMemory = async (s) => {
    setSuggestions((prev) => prev.filter((x) => x.suggestion_id !== s.suggestion_id));
    try { await api.post(`/memory-suggestions/${s.suggestion_id}/dismiss`); } catch {}
    finally { refreshBadge(); }
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/companion/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (res.status === 402) {
        let detail = "You've reached today's free message limit.";
        try { detail = (await res.json()).detail || detail; } catch {}
        setMessages((m) => m.slice(0, -2));
        setInput(text);
        toast.error(detail, {
          action: { label: "Upgrade", onClick: () => navigate("/app/subscription") },
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
          if (payload.suggestions) {
            setSuggestions((prev) => [...payload.suggestions, ...prev]);
            refreshBadge();
          }
          if (payload.support?.level) {
            setSupport(payload.support.level);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I couldn't respond just now. Please try again in a moment." };
        return copy;
      });
    } finally {
      setStreaming(false);
      if (!premium) refreshSub();
    }
  };

  return (
    <div className="relative -mt-2 flex gap-6">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 opacity-[0.12]">
        <img src={TEXTURE} alt="" className="h-full w-full object-cover" />
      </div>

      <div className="flex h-[calc(100vh-9rem)] min-w-0 flex-1 flex-col">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary animate-breathe">
          <HeartPulse className="h-5 w-5 text-primary-foreground" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-serif text-xl">{t("comp.header")}</h1>
          <p className="text-xs text-muted-foreground">{t("comp.sub")}</p>
        </div>
        <div className="ml-auto">
          <PrivacyNote align="end" />
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto rounded-3xl border border-border/40 bg-card/30 p-5 no-scrollbar" data-testid="companion-messages">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <p className="max-w-sm font-serif text-2xl leading-snug">{t("comp.emptyTitle")}</p>
            <p className="mt-2 max-w-xs text-sm text-muted-foreground">{t("comp.emptySub")}</p>
            {starters.length > 0 && (
              <div className="mt-6 flex max-w-md flex-wrap justify-center gap-2" data-testid="companion-starters">
                {starters.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => send(s)}
                    disabled={streaming}
                    data-testid={`companion-starter-${i}`}
                    className="rounded-full border border-border/60 bg-card/60 px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} role={m.role}>{m.content}</Bubble>)}
        <div ref={endRef} />
      </div>

      <AnimatePresence>
        {suggestions.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                      className="mt-4 overflow-hidden" data-testid="memory-suggestions">
            <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
              {suggestions.map((s) => (
                <motion.div key={s.suggestion_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 20 }}
                            className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-3">
                  <Brain className="h-4 w-4 flex-shrink-0 text-primary" strokeWidth={1.5} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-primary">Remember this?</p>
                    <p className="text-sm">{s.content}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" className="h-8 rounded-full gap-1 text-sage hover:bg-sage/10" onClick={() => acceptMemory(s)} data-testid={`accept-memory-${s.suggestion_id}`}>
                      <Check className="h-4 w-4" strokeWidth={1.5} /> Save
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 rounded-full p-0 text-muted-foreground" onClick={() => dismissMemory(s)} data-testid={`dismiss-memory-${s.suggestion_id}`}>
                      <X className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {support && <SupportCard level={support} onDismiss={() => setSupport(null)} />}

      <div className="mt-4 flex items-end gap-2 rounded-3xl border border-border/60 bg-card p-2">
        <Textarea
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t("comp.placeholder")} data-testid="companion-input"
          className="min-h-11 max-h-40 resize-none border-0 bg-transparent focus-visible:ring-0 shadow-none"
        />
        <Button onClick={() => send()} disabled={streaming || !input.trim()} size="icon" className="h-11 w-11 flex-shrink-0 rounded-full" data-testid="companion-send-button">
          <Send className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      </div>
      {remaining !== null && (
        <div className="mt-2 flex items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="free-chat-counter">
          {remaining > 0 ? (
            <span>{remaining} of {sub.chat_limit} free messages left today</span>
          ) : (
            <span>You've used today's free messages.</span>
          )}
          <button onClick={() => navigate("/app/subscription")} className="inline-flex items-center gap-1 font-medium text-primary hover:underline" data-testid="companion-upgrade-link">
            <Sparkles className="h-3 w-3" strokeWidth={1.5} /> Go unlimited
          </button>
        </div>
      )}
      </div>
      <aside className="hidden w-80 flex-shrink-0 lg:block" data-testid="companion-right-rail">
        <QuotePanel variant="rail" />
      </aside>
    </div>
  );
}
